import React from "react";
import {
  Input,
  Button,
  Textarea,
  Switch,
} from "@my-scoot/component-library-legacy";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { api, dataProvider } from "data";
import constants from "constants/constants";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { refreshIframe } from "./CreatorMiscellaneousData/Utils";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import {
  MiscellaneousConfig,
  TEMPLATE_KEYS,
  SIDEBAR_SECTIONS,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
  OFFERING_SECTION_FORM_DATA,
  ABOUT_YOU_CONFIG,
} from "ui/pages/TemplatePreview/constants";
import useSetMissceleneous from "./common/useSetMissceleneous";
import useTemplatePreview from "ui/pages/TemplatePreview/hooks/useTemplatePreview";
import { isRequestSuccessful } from "utils/Utils";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "./hideShow";
import useVisibilityMutations from "../useVisibilityMutations.hook";
import { handleVisibilityToggle } from "../utils";
import { UPDATE_CREATOR_DATA_API } from "features/Common/modules/CreatorData/CreatorData.constants";
import { getUserID } from "utils/AuthUtil";
import { logError } from "utils/error";
import {
  DEFAULT_TEXT_OFFERINGS,
  OFFERINGS_SECTION_LABELS,
  OFFERINGS_SECTION_PLACEHOLDERS,
  OFFERINGS_SECTION_MESSAGES,
  OFFERINGS_SECTION_DEFAULTS,
  SHOW_RATINGS_MISC_KEY,
  OFFERINGS_SECTION_FIELD_NAMES,
} from "../sidebar.constant";
import { useOfferingsSectionHandlers } from "./OfferingSection/utils/useOfferingsSectionHandlers";
import { useShowListingRatingsToggle } from "./OfferingSection/utils/useShowListingRatingsToggle";
import classnames from "classnames";
import style from "./../style.module.css";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";

const formData = OFFERING_SECTION_FORM_DATA[TEMPLATE_KEYS.UNLOCK];

export const OfferingsSection = ({
  disableAllEdits,
  setField,
  hiddenFields = [],
}) => {
  const { notify } = useNotifications();
  const initial_nav_section_name_ref = React.useRef(null);
  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);
  const { pushNotif } = useTemplatePreview();
  const [section_name, set_section_name] = React.useState(
    OFFERINGS_SECTION_DEFAULTS.SECTION_NAME
  );
  const [nav_section_name, set_nav_section_name] = React.useState(
    DEFAULT_TEXT_OFFERINGS
  );
  const [uuid, set_uuid] = React.useState(null);
  const [processing, set_processing] = React.useState(false);
  const [data, set_data] = React.useState({
    heading: OFFERINGS_SECTION_DEFAULTS.HEADING,
  });
  const [error_object, set_error_object] = React.useState({});
  const [visible, set_visible] = React.useState(true);
  const { createMutation, updateMutation } = useVisibilityMutations();

  const updateData = (value, key) => {
    set_data((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const {
    handleSectionNameChange,
    handleNavSectionNameChange,
    handleNavSectionNameBlur,
    handleHeadingChange,
    handleDescriptionChange,
  } = useOfferingsSectionHandlers({
    set_section_name,
    nav_section_name,
    set_nav_section_name,
    updateData,
    setField,
    set_error_object,
  });

  React.useEffect(() => {
    assignData();
  }, []);

  const {
    account_show_ratings,
    handleShowRatingsToggle,
    isShowRatingsLoading,
    isShowRatingsUpdating,
  } = useShowListingRatingsToggle({
    updateData,
    miscKey: SHOW_RATINGS_MISC_KEY,
    miscValue: data?.[SHOW_RATINGS_MISC_KEY],
  });

  React.useEffect(() => {
    if (!checkIsArrayEmpty(miscellaneous_data?.offeringsSection)) {
      const [offeringsData] = miscellaneous_data.offeringsSection;
      if (offeringsData?.uuid) {
        set_uuid(offeringsData?.uuid);
      }
    }
  }, [miscellaneous_data]);

  useSetMissceleneous({
    listeners: [data, section_name, nav_section_name, uuid, visible],
    data: {
      ...miscellaneous_data,
      offeringsSection: [
        {
          ...(uuid && { uuid }),
          data: {
            section_name,
            offerings_title: nav_section_name,
            visible,
            data: {
              description: data?.description,
              heading: data?.heading,
              [SHOW_RATINGS_MISC_KEY]: data?.[SHOW_RATINGS_MISC_KEY],
            },
          },
        },
      ],
    },
    updateHandler: {
      offeringsSection: {
        handleSave: () => handleSave(),
      },
    },
  });

  const assignData = () => {
    try {
      if (checkIsArrayEmpty(miscellaneous_data?.offeringsSection)) return;
      const temp = miscellaneous_data?.offeringsSection?.[0];
      let initial_data = { ...temp?.data?.data };
      initial_data = {
        ...initial_data,
        ...(!initial_data?.heading && {
          heading: OFFERINGS_SECTION_DEFAULTS.HEADING,
        }),
      };
      set_data(initial_data);
      set_section_name(
        temp?.data?.section_name || OFFERINGS_SECTION_DEFAULTS.SECTION_NAME
      );
      set_nav_section_name(
        temp?.data?.offerings_title || DEFAULT_TEXT_OFFERINGS
      );
      initial_nav_section_name_ref.current =
        temp?.data?.offerings_title || DEFAULT_TEXT_OFFERINGS;
      set_uuid(temp?.uuid);
      set_visible(temp?.data?.visible ?? true);
    } catch (err) {
      logError(OFFERINGS_SECTION_MESSAGES.ASSIGN_ERROR_LOG, err);
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        notification_color_keys.error
      );
    }
  };

  const updateNavbarTitleMutation = useCustomMutation({
    url: `${UPDATE_CREATOR_DATA_API}/${getUserID()}`,
    onSuccess: () => {
      initial_nav_section_name_ref.current = nav_section_name;
    },
    onError: (error) => {
      logError({
        error: OFFERINGS_SECTION_MESSAGES.NAVBAR_UPDATE_ERROR,
        details: error,
        occuredAt: OFFERINGS_SECTION_MESSAGES.ERROR_LOCATION,
        when: OFFERINGS_SECTION_MESSAGES.ERROR_WHEN,
      });
      set_error_object((prev) => ({
        ...prev,
        nav_section_name: OFFERINGS_SECTION_MESSAGES.NAVBAR_UPDATE_FAILED,
      }));
    },
  });

  const handleSave = async () => {
    try {
      if (processing) return;

      const errors = {};
      if (
        !data?.heading?.trim() &&
        showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.SECTION_TITLE)
      ) {
        errors.heading = "Heading is required";
      }
      if (
        !data?.description?.trim() &&
        showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.DESCRIPTION)
      ) {
        errors.description = "Description is required";
      }
      if (
        !nav_section_name?.trim() &&
        showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.NAVBAR_TITLE)
      ) {
        errors.nav_section_name = "Navbar title is required";
      }

      if (Object.keys(errors).length) {
        set_error_object(errors);
        notify(
          OFFERINGS_SECTION_MESSAGES.INVALID_FORM,
          notification_color_keys.error
        );
        return;
      }

      set_processing(true);
      set_error_object({});

      let payload, api_resource;
      let temp = data;
      if (temp?.draft) {
        delete temp.draft;
      }

      let api_uuid = uuid;
      if (!checkIsArrayEmpty(miscellaneous_data?.offeringsSection)) {
        const [offeringsData] = miscellaneous_data.offeringsSection;
        if (offeringsData?.uuid) {
          api_uuid = offeringsData.uuid;
        }
      }

      if (nav_section_name !== initial_nav_section_name_ref.current) {
        await updateNavbarTitleMutation.mutateAsync({
          source: OFFERINGS_SECTION_DEFAULTS.SOURCE,
          updated_values: {
            lead_keys: {
              offerings_title: nav_section_name,
            },
          },
        });
      }

      if (checkIsArrayEmpty(api_uuid)) {
        api_resource = api.creator_miscellaneous_data.create;
        payload = {
          type: creator_miscellaneous_data_types.OFFERING_SECTION,
          data: {
            section_name,
            offerings_title: nav_section_name,
            visible,
            data: temp,
          },
        };
      } else {
        api_resource = api.creator_miscellaneous_data.update;
        payload = {
          uuid: api_uuid,
          updated_values: {
            data: {
              section_name,
              offerings_title: nav_section_name,
              visible,
              data: temp,
            },
          },
        };
      }

      const status = await dataProvider.custom_request(
        api_resource,
        OFFERINGS_SECTION_DEFAULTS.REQUEST_METHOD,
        payload
      );

      if (isRequestSuccessful(status.status)) {
        refreshIframe();
        set_data({ ...temp });
        set_error_object({});

        if (checkIsArrayEmpty(uuid)) {
          fetchMiscellaneousData({
            type: creator_miscellaneous_data_types.OFFERING_SECTION,
          });
        } else {
          const context_data = Array.isArray(
            miscellaneous_data?.offeringsSection
          )
            ? [...miscellaneous_data.offeringsSection]
            : [];

          context_data[0] = {
            ...(context_data?.[0] || {}),
            data: {
              section_name,
              offerings_title: nav_section_name,
              data: temp,
            },
          };

          set_miscellaneous_data({
            ...miscellaneous_data,
            offeringsSection: context_data,
          });
        }
      }

      pushNotif(status?.message);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        notification_color_keys.error
      );
    } finally {
      set_processing(false);
    }
  };

  const showSectionTitle = (key) => {
    return !hiddenFields.includes(key);
  };

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.OFFERINGS_SECTION],
      sectionType: creator_miscellaneous_data_types.OFFERING_SECTION,
      defaultSectionName:
        SIDEBAR_SECTIONS[SIDEBAR_KEYS.OFFERINGS_SECTION].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation,
      updateMutation,
    });

  return (
    <div>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      {showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.SECTION_TITLE) && (
        <div>
          <div className={classnames(style.label, "mb-1")}>
            {OFFERINGS_SECTION_LABELS.SECTION_TITLE}
          </div>

          <Input
            hasPopover={false}
            fullWidth
            value={section_name}
            error={error_object.section_name}
            helperText={error_object?.section_name}
            inputProps={{ maxLength: MiscellaneousConfig.maxTitleLength }}
            onChange={handleSectionNameChange}
            disabled={disableAllEdits}
          />
        </div>
      )}

      {showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.NAVBAR_TITLE) && (
        <div>
          <div className="tw-font-bold tw-text-[#666] tw-tracking-[0.2px] tw-text-sm tw-mb-1 tw-mt-3">
            {OFFERINGS_SECTION_LABELS.NAVBAR_TITLE}
          </div>

          <Input
            hasPopover={false}
            fullWidth
            value={nav_section_name}
            error={error_object.nav_section_name}
            helperText={error_object?.nav_section_name}
            inputProps={{ maxLength: ABOUT_YOU_CONFIG.MAX_NAVBAR_TITLE_LENGTH }}
            onChange={handleNavSectionNameChange}
            onBlur={handleNavSectionNameBlur}
            disabled={disableAllEdits}
          />
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-between tw-mt-3">
        <span className="tw-text-sm tw-font-bold tw-text-[#666] tw-tracking-[0.2px]">
          {OFFERINGS_SECTION_LABELS.SHOW_RATINGS}
        </span>

        <Switch
          checked={
            account_show_ratings ?? data?.[SHOW_RATINGS_MISC_KEY] ?? false
          }
          disabled={
            disableAllEdits || isShowRatingsLoading || isShowRatingsUpdating
          }
          onChange={handleShowRatingsToggle}
          color="success"
          size="large"
        />
      </div>

      {showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.HEADING) && (
        <div>
          <div className="tw-font-bold tw-text-[#666] tw-tracking-[0.2px] tw-text-sm tw-mt-3 tw-mb-1 after:tw-ml-1 after:tw-content-['*'] after:tw-text-[#e83f6f] after:tw-text-base after:tw-leading-3">
            {formData.heading.label}
          </div>
          <Input
            hasPopover={false}
            error={error_object?.heading}
            placeholder={OFFERINGS_SECTION_PLACEHOLDERS.HEADING}
            className="tw-placeholder:text-black/50 tw-placeholder:text-sm tw-placeholder:opacity-100"
            value={data?.heading}
            helperText={error_object?.heading}
            fullWidth
            inputProps={{ maxLength: MiscellaneousConfig.maxTitleLength }}
            onChange={handleHeadingChange}
          />
        </div>
      )}

      {showSectionTitle(OFFERINGS_SECTION_FIELD_NAMES.DESCRIPTION) && (
        <div>
          <div className="tw-font-bold tw-text-[#666] tw-tracking-[0.2px] tw-text-sm tw-mt-3 tw-mb-1 after:tw-ml-1 after:tw-content-['*'] after:tw-text-[#e83f6f] after:tw-text-base after:tw-leading-3">
            {formData.description.label}
          </div>
          <Textarea
            error={error_object?.description}
            helperText={error_object?.description}
            placeholder={OFFERINGS_SECTION_PLACEHOLDERS.DESCRIPTION}
            value={data?.description}
            rows={OFFERINGS_SECTION_DEFAULTS.TEXTAREA_ROWS}
            customClasses={{
              wrapper:
                "tw-placeholder:text-black/50 tw-placeholder:text-sm tw-placeholder:opacity-100",
            }}
            maxLength={MiscellaneousConfig.maxDescLength}
            onChange={handleDescriptionChange}
            minWidth={OFFERINGS_SECTION_DEFAULTS.TEXTAREA_MIN_WIDTH}
          />
        </div>
      )}

      <Button
        variant="contained"
        color="primary"
        disableElevation
        disabled={processing || disableAllEdits}
        loading={processing}
        onClick={handleSave}
        className="!tw-my-4 tw-float-right"
      >
        {OFFERINGS_SECTION_LABELS.SAVE_BUTTON}
      </Button>
    </div>
  );
};
